import { gql, TypedDocumentNode } from '@apollo/client';

import {
  TimeEntriesConnectionInput,
  TimeEntriesTotalDurationInput,
} from '@atom/types/timeEntry';
import {
  UserBudgetCreateInput,
  UserBudgetDeleteInput,
  UserBudgetUpdateInput,
  UserCertificationCreateInput,
  UserCertificationDeleteInput,
  UserCertificationUpdateInput,
  UserDetail,
  UserInput,
  UsersConnection,
  UsersConnectionInput,
  UserUpdateInput,
} from '@atom/types/user';

import { BASE_ASSIGNED_ROLE } from './role';
import { TIME_SHEET_ENTRIES } from './timeEntry';

export const BASE_USER_FRAGMENT = gql`
  fragment BaseUser on User {
    id
    employeeId
    baseLocation
    firstName
    lastName
    isVerified
    isActive
    photoUrl
    department
    title
    role
    level
    email
    location
    rate
    notifications
    timezoneLocation
    createdDate
    updatedDate
    updatedBy {
      firstName
      lastName
    }
    status
    budgets {
      id
      externalId
      name
      rate
      restored
      reopenedWorkOrderIds
    }
    certifications {
      id
      name
      description
    }
    defaultUserGroup {
      id
    }
    userGroups {
      id
      name
      groupPath
      certifications {
        id
        name
        description
      }
      budgets {
        id
        name
        rate
        restored
        reopenedWorkOrderIds
      }
    }
    timeEntryApproverInfo {
      id
      name
      colorId
      groupPath
      actions
    }
    roles {
      ...BaseAssignedRole
    }
    inheritedRoles {
      ...BaseAssignedRole
    }
  }
  ${BASE_ASSIGNED_ROLE}
`;

export const GET_USER: TypedDocumentNode<
  { user: UserDetail },
  { id: string; input?: UserInput }
> = gql`
  query user($id: ID!, $input: UserInput) {
    user(id: $id, input: $input) {
      ...BaseUser
    }
  }
  ${BASE_USER_FRAGMENT}
`;

export const GET_USERS: TypedDocumentNode<
  { users: UsersConnection },
  { input: UsersConnectionInput }
> = gql`
  query users($input: UsersConnectionInput!) {
    users(input: $input) {
      totalCount
      users {
        ...BaseUser
      }
    }
  }
  ${BASE_USER_FRAGMENT}
`;

export const GET_USERS_WITH_TIME_SHEET_ENTRIES: TypedDocumentNode<
  { users: UsersConnection },
  {
    input: UsersConnectionInput;
    timeSheetEntriesInput: TimeEntriesConnectionInput;
    timeEntriesTotalDurationInput: TimeEntriesTotalDurationInput;
  }
> = gql`
  query users(
    $input: UsersConnectionInput!
    $timeSheetEntriesInput: TimeEntriesConnectionInput!
    $timeEntriesTotalDurationInput: TimeEntriesTotalDurationInput!
  ) {
    users(input: $input) {
      totalCount
      users {
        ...BaseUser
        timeEntries(input: $timeSheetEntriesInput) {
          ...TimeSheetEntries
        }
        timeEntriesTotalDuration(input: $timeEntriesTotalDurationInput)
      }
    }
  }
  ${BASE_USER_FRAGMENT}
  ${TIME_SHEET_ENTRIES}
`;

export const USER_UPDATE: TypedDocumentNode<
  { userUpdate: UserDetail },
  { input: UserUpdateInput }
> = gql`
  mutation userUpdate($input: UserUpdateInput!) {
    userUpdate(input: $input) {
      ...BaseUser
    }
  }
  ${BASE_USER_FRAGMENT}
`;

export const USER_DELETE: TypedDocumentNode<
  { userDelete: boolean },
  { id: string }
> = gql`
  mutation userDelete($id: ID!) {
    userDelete(id: $id)
  }
`;

export const CREATE_USER_BUDGET: TypedDocumentNode<
  { userBudgetCreate: UserDetail },
  { input: UserBudgetCreateInput }
> = gql`
  mutation userBudgetCreate($input: UserBudgetCreateInput!) {
    userBudgetCreate(input: $input) {
      ...BaseUser
    }
  }
  ${BASE_USER_FRAGMENT}
`;

export const UPDATE_USER_BUDGET: TypedDocumentNode<
  { userBudgetUpdate: UserDetail },
  { input: UserBudgetUpdateInput }
> = gql`
  mutation userBudgetUpdate($input: UserBudgetUpdateInput!) {
    userBudgetUpdate(input: $input) {
      ...BaseUser
    }
  }
  ${BASE_USER_FRAGMENT}
`;

export const DELETE_USER_BUDGET: TypedDocumentNode<
  { userBudgetDelete: UserDetail },
  { input: UserBudgetDeleteInput }
> = gql`
  mutation userBudgetDelete($input: UserBudgetDeleteInput!) {
    userBudgetDelete(input: $input) {
      ...BaseUser
    }
  }
  ${BASE_USER_FRAGMENT}
`;

export const CREATE_USER_CERTIFICATION: TypedDocumentNode<
  { userCertificationCreate: UserDetail },
  { input: UserCertificationCreateInput }
> = gql`
  mutation userCertificationCreate($input: UserCertificationCreateInput!) {
    userCertificationCreate(input: $input) {
      ...BaseUser
    }
  }
  ${BASE_USER_FRAGMENT}
`;

export const UPDATE_USER_CERTIFICATION: TypedDocumentNode<
  { userCertificationUpdate: UserDetail },
  { input: UserCertificationUpdateInput }
> = gql`
  mutation userCertificationUpdate($input: UserCertificationUpdateInput!) {
    userCertificationUpdate(input: $input) {
      ...BaseUser
    }
  }
  ${BASE_USER_FRAGMENT}
`;

export const DELETE_USER_CERTIFICATION: TypedDocumentNode<
  { userCertificationDelete: UserDetail },
  { input: UserCertificationDeleteInput }
> = gql`
  mutation userCertificationDelete($input: UserCertificationDeleteInput!) {
    userCertificationDelete(input: $input) {
      ...BaseUser
    }
  }
  ${BASE_USER_FRAGMENT}
`;
